/**
 * @fileoverview REST API Client for the Hotline application
 * Provides a type-safe interface to all backend API endpoints
 */

import * as Types from "./types";
import * as Constants from "./constants";

/**
 * Configuration for the API client
 */
export interface ApiClientConfig {
  baseUrl?: string;
  apiKey?: string;
  timeout?: number;
  headers?: Record<string, string>;
}

/**
 * API Client for Hotline REST API
 * 
 * This client provides a complete type-safe interface to all backend endpoints,
 * replacing the Firebase Functions approach with standard REST calls.
 * 
 * Token persistence is handled automatically using localStorage.
 * 
 * @example
 * const client = new GetSeriousApiClient({
 *   baseUrl: 'https://api.getserious.com'
 * });
 * 
 * // Login automatically persists the token
 * await client.auth.login('user@example.com', 'password');
 * 
 * // Token is automatically loaded from localStorage on subsequent page loads
 * // and included in all authenticated requests
 */
export class GetSeriousApiClient {
  private static readonly TOKEN_STORAGE_KEY = 'gs_auth_token';
  
  private baseUrl: string;
  private apiKey?: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;
  private authToken?: string;

  constructor(config: ApiClientConfig) {
    // Default to current hostname if baseUrl not provided
    const defaultBaseUrl = typeof window !== 'undefined' 
      ? `${window.location.protocol}//${window.location.host}`
      : 'http://localhost:3000';
    
    this.baseUrl = (config.baseUrl || defaultBaseUrl).replace(/\/$/, ""); // Remove trailing slash
    console.log(`API Client initialized with baseUrl: ${this.baseUrl}`);
    this.apiKey = config.apiKey;
    this.timeout = config.timeout ?? 30000;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      ...(config.headers || {}),
    };
    
    // Load token from localStorage on initialization
    this.loadTokenFromStorage();
  }

  /**
   * Load auth token from localStorage
   * @internal
   */
  private loadTokenFromStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const storedToken = localStorage.getItem(GetSeriousApiClient.TOKEN_STORAGE_KEY);
      if (storedToken) {
        this.authToken = storedToken;
      }
    } catch (e) {
      console.error('Failed to load auth token from storage:', e);
    }
  }

  /**
   * Save auth token to localStorage
   * @internal
   */
  private saveTokenToStorage(token: string): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(GetSeriousApiClient.TOKEN_STORAGE_KEY, token);
    } catch (e) {
      console.error('Failed to save auth token to storage:', e);
    }
  }

  /**
   * Remove auth token from localStorage
   * @internal
   */
  private removeTokenFromStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.removeItem(GetSeriousApiClient.TOKEN_STORAGE_KEY);
    } catch (e) {
      console.error('Failed to remove auth token from storage:', e);
    }
  }

  /**
   * Set the authentication token for subsequent requests
   * Token is automatically persisted to localStorage
   */
  setAuthToken(token: string): void {
    this.authToken = token;
    this.saveTokenToStorage(token);
  }

  /**
   * Get the current authentication token
   */
  getAuthToken(): string | undefined {
    return this.authToken;
  }

  /**
   * Check if client has an auth token
   */
  isAuthenticated(): boolean {
    return !!this.authToken;
  }

  /**
   * Clear the authentication token (e.g., on logout)
   * Token is automatically removed from localStorage
   */
  clearAuthToken(): void {
    this.authToken = undefined;
    this.removeTokenFromStorage();
  }

  /**
   * Makes an HTTP request with the provided configuration
   * @internal
   */
  private async request<T>(
    method: string,
    path: string,
    body?: any,
    options?: {
      headers?: Record<string, string>;
      params?: Record<string, string | number | boolean>;
    }
  ): Promise<T> {
    const url = new URL(path, this.baseUrl);
    
    // Add query parameters
    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    const headers: Record<string, string> = {
      ...this.defaultHeaders,
      ...(options?.headers || {}),
    };

    // Use authToken first (from login), then fall back to apiKey
    const token = this.authToken || this.apiKey;
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const fetchOptions: RequestInit = {
      method,
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };

    if (body) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url.toString(), fetchOptions);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiClientError(
        response.status,
        error.code || "unknown-error",
        error.message || `HTTP ${response.status}`,
        error.details
      );
    }

    const data = await response.json();
    return data.data || data; // Support both wrapped and unwrapped responses
  }

  /**
   * User-facing public endpoints
   */
  readonly user = {
    /**
     * Submit a new take (text or voice)
     * Public endpoint - no authentication required
     */
    submit: async (
      payload: Types.SubmitTakeRequest
    ): Promise<Types.SubmitTakeResponse> => {
      return this.request(
        "POST",
        "/api/user/submit",
        { data: payload }
      );
    },

    /**
     * Add a phone number to an existing submission
     * Public endpoint - no authentication required
     */
    addPhoneNumber: async (
      payload: Types.AddPhoneNumberRequest
    ): Promise<Types.AddPhoneNumberResponse> => {
      return this.request(
        "POST",
        "/api/user/add-phone",
        { data: payload }
      );
    },

    /**
     * Mark a submission as viewed by the user
     * Public endpoint - no authentication required
     */
    markAsViewed: async (
      payload: Types.MarkAsViewedRequest
    ): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "POST",
        `/api/user/${payload.submissionId}/mark-viewed`
      );
    },

    /**
     * Get hydrated state for homepage
     * Fetches site controls, ratings, episode, site settings, and user takes in one call
     * Public endpoint - no authentication required
     */
    getHydratedState: async (
      payload: Types.GetHydratedStateRequest
    ): Promise<Types.GetHydratedStateResponse> => {
      const params: Record<string, string> = {
        showSlug: payload.showSlug,
      };

      if (payload.userTakesIds && payload.userTakesIds.length > 0) {
        params.userTakesIds = payload.userTakesIds.join(',');
      }

      return this.request("GET", "/api/user/hydrated-state", undefined, { params });
    },
  };

  /**
   * Standard authenticated user endpoints
   */
  readonly standard = {
    /**
     * Get list of shows available to the authenticated user
     * Returns shows based on user's ACL (admin sees all, others see what they're assigned to)
     * Authenticated endpoint
     */
    getAvailableShows: async (): Promise<Types.GetAvailableShowsResponse> => {
      return this.request("GET", "/api/standard/shows");
    },
  };

  /**
   * Admin endpoints
   */
  readonly admin = {
    /**
     * Create a new show
     * Admin-only endpoint
     */
    createShow: async (
      payload: Types.CreateNewShowRequest
    ): Promise<Types.CreateNewShowResponse> => {
      return this.request("POST", "/api/admin/shows", { data: payload });
    },

    /**
     * Update an existing show
     * Admin-only endpoint
     * Uses the same endpoint as createShow but with an id in the payload
     */
    updateShow: async (
      payload: Types.UpdateShowRequest
    ): Promise<Types.CreateNewShowResponse> => {
      return this.request("POST", "/api/admin/shows", { data: payload });
    },

    /**
     * Get hydrated state for the admin dashboard in a single request
     * Aggregates themes, active site information, and metadata for fast bootstrapping
     */
    getHydratedState: async (
      payload?: Types.GetAdminHydratedStateRequest
    ): Promise<Types.GetAdminHydratedStateResponse> => {
      const params: Record<string, string | number | boolean> = {};

      if (payload?.includeBadges !== undefined) {
        params.includeBadges = payload.includeBadges;
      }

      return this.request("GET", "/api/admin/hydrated-state", undefined, { params });
    },

    /**
     * Create a new theme
     * Admin-only endpoint
     */
    createTheme: async (
      payload: Types.CreateThemeRequest
    ): Promise<Types.CreateThemeResponse> => {
      return this.request(
        "POST",
        `/api/admin/themes`,
        { data: payload }
      );
    },

    /**
     * Update a theme (settings + badges) for a show
     * Admin-only endpoint
     */
    updateTheme: async (
      payload: Types.UpdateThemeRequest
    ): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "PATCH",
        `/api/admin/themes`,
        { data: payload }
      );
    },

    /**
     * Create a new user
     * Admin-only endpoint
     */
    createUser: async (
      payload: Types.CreateUserRequest
    ): Promise<Types.CreateUserResponse> => {
      return this.request(
        "POST",
        `/api/admin/users`,
        { data: payload }
      );
    },

    /**
     * List all users
     * Admin-only endpoint
     */
    listUsers: async (): Promise<Types.ListUsersResponse> => {
      return this.request("GET", `/api/admin/users`);
    },

    /**
     * Delete a user by id
     * Admin-only endpoint
     */
    deleteUser: async (userId: string): Promise<Types.DeleteUserResponse> => {
      return this.request(
        "DELETE",
        `/api/admin/users/${userId}`
      );
    },

    /**
     * Update an existing user
     * Admin-only endpoint
     */
    updateUser: async (
      userId: string,
      payload: Types.UpdateUserRequest
    ): Promise<Types.UpdateUserResponse> => {
      return this.request(
        "PATCH",
        `/api/admin/users/${userId}`,
        { data: payload }
      );
    },

    /**
     * Get global statistics
     * Admin-only endpoint
     */
    getGlobalStats: async (): Promise<Types.GlobalStats> => {
      return this.request("GET", "/api/admin/stats");
    },

    /**
     * Force recalculation of statistics
     * Admin-only endpoint
     */
    recalculateStats: async (): Promise<Types.ForceStatRecalculationResponse> => {
      return this.request("POST", "/api/admin/stats/recalculate", {});
    },

    /**
     * Purge all submissions and associated assets for a specific episode (DESTRUCTIVE - NO UNDO)
     * Admin-only endpoint
     */
    purgeSubmissionsByEpisode: async (episodeId: string): Promise<Types.PurgeResponse> => {
      return this.request("POST", `/api/admin/purge/episode/${episodeId}`, {});
    },

    /**
     * Clean up dangling assets not attached to any submissions
     * Admin-only endpoint
     */
    cleanupDanglingAssets: async (): Promise<Types.CleanupDanglingAssetsResponse> => {
      return this.request("POST", "/api/admin/cleanup/dangling-assets", {});
    },

    /**
     * Migrate existing assets for a given episode to add submissionId references
     * Admin-only endpoint
     */
    migrateAssetsForEpisode: async (episodeId: string): Promise<Types.MigrateAssetsResponse> => {
      return this.request("POST", `/api/admin/migrate/assets/${episodeId}`, {});
    },
  };

  /**
   * Producer endpoints
   */
  readonly producer = {
    /**
     * Get hydrated state for the producer dashboard in a single request
     * Aggregates show, site settings, site controls, and ratings for fast bootstrapping
     * Episodes are fetched separately
     * Producer-only endpoint
     */
    getHydratedState: async (
      payload: Types.GetProducerHydratedStateRequest
    ): Promise<Types.GetProducerHydratedStateResponse> => {
      const params: Record<string, string | number | boolean> = {
        showSlug: payload.showSlug,
      };

      return this.request("GET", "/api/producer/hydrated-state", undefined, { params });
    },

    /**
     * Get hydrated state for the show admin dashboard in a single request
     * Aggregates show metadata, episodes, site controls, and available themes
     * Producer-only endpoint
     */
    getShowAdminHydratedState: async (
      payload: Types.GetShowAdminHydratedStateRequest
    ): Promise<Types.GetShowAdminHydratedStateResponse> => {
      const params: Record<string, string | number | boolean> = {
        showSlug: payload.showSlug,
      };

      return this.request("GET", "/api/producer/show-admin/hydrated-state", undefined, { params });
    },

    /**
     * Update site controls for a show
     * Producer-only endpoint
     */
    updateSiteControls: async (
      payload: Types.UpdateSiteControlsRequest
    ): Promise<Types.RatingOperationResponse> => {
      return this.request("PATCH", "/api/producer/site-controls", { data: payload });
    },

    /**
     * Set the active theme for a show
     * Producer-only endpoint
     */
    setShowTheme: async (
      payload: Types.SetShowThemeRequest
    ): Promise<Types.RatingOperationResponse> => {
      return this.request("POST", `/api/producer/shows/${payload.showId}/theme`, { data: payload });
    },

    /**
     * Search submissions for the producer dashboard
     * Producer-only endpoint - includes spam and inappropriate submissions
     */
    /**
     * Search submissions with filters
     * Producer-only endpoint - includes spam and inappropriate submissions
     */
    searchSubmissions: async (options: {
      showId: string;
      episodeId: string;
      limit?: number;
      offset?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      filter_a?: string[];
      filter_b?: string[];
      filter_c?: string[];
      filter_d?: string[];
      searchText?: string;
      isVoice?: boolean;
      isFavorited?: boolean;
      hasPhoneNumber?: boolean;
      isRated?: boolean;
      minScore?: number;
    }): Promise<Types.SubmissionSearchResponse> => {
      const params: Record<string, string | number | boolean> = {
        showId: options.showId,
        episodeId: options.episodeId,
      };

      if (options.limit) params.limit = options.limit;
      if (options.offset) params.offset = options.offset;
      if (options.sortBy) params.sortBy = options.sortBy;
      if (options.sortOrder) params.sortOrder = options.sortOrder;

      // Add filter parameters
      if (options.filter_a && options.filter_a.length > 0) {
        params.filter_a = options.filter_a.join(',');
      }
      if (options.filter_b && options.filter_b.length > 0) {
        params.filter_b = options.filter_b.join(',');
      }
      if (options.filter_c && options.filter_c.length > 0) {
        params.filter_c = options.filter_c.join(',');
      }
      if (options.filter_d && options.filter_d.length > 0) {
        params.filter_d = options.filter_d.join(',');
      }
      if (options.searchText) {
        params.searchText = options.searchText;
      }
      if (options.isVoice !== undefined) {
        params.isVoice = options.isVoice;
      }
      if (options.isFavorited !== undefined) {
        params.isFavorited = options.isFavorited;
      }
      if (options.hasPhoneNumber !== undefined) {
        params.hasPhoneNumber = options.hasPhoneNumber;
      }
      if (options.isRated !== undefined) {
        params.isRated = options.isRated;
      }
      if (options.minScore !== undefined) {
        params.minScore = options.minScore;
      }

      return this.request("GET", "/api/producer/submissions/search", undefined, { params });
    },

    /**
     * Create a new episode within a show
     * Producer-only endpoint
     */
    createEpisode: async (
      payload: Types.CreateNewEpisodeRequest
    ): Promise<Types.CreateNewEpisodeResponse> => {
      return this.request("POST", `/api/producer/episodes`, { data: payload });
    },

    /**
     * Set the active episode within a show
     * Producer-only endpoint
     */
    setActiveEpisode: async (
      payload: Types.SetActiveEpisodeRequest
    ): Promise<Types.RatingOperationResponse> => {
      return this.request("POST", `/api/producer/episodes/${payload.episodeId}/set-active`, { data: payload });
    },

    /**
     * Update episode details
     * Producer-only endpoint
     */
    updateEpisode: async (
      payload: Types.UpdateEpisodeDetailsRequest
    ): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "PATCH",
        `/api/producer/episodes/${payload.episodeId}`,
        { data: payload }
      );
    },

    /**
     * Delete an episode
     * Producer-only endpoint
     */
    deleteEpisode: async (
      episodeId: string
    ): Promise<Types.DeleteOperationResponse> => {
      return this.request("DELETE", `/api/producer/episodes/${episodeId}`);
    },

    /**
     * Delete episode's topic image
     * Producer-only endpoint
     */
    deleteEpisodeTopicImage: async (
      episodeId: string
    ): Promise<Types.DeleteOperationResponse> => {
      return this.request("DELETE", `/api/producer/episodes/${episodeId}/topic-image`);
    },

    /**
     * Get episode statistics
     * Producer-only endpoint
     */
    getEpisodeStats: async (
      episodeId: string
    ): Promise<Types.GetEpisodeStatsResponse> => {
      return this.request("GET", `/api/producer/episodes/${episodeId}/stats`);
    },

    /**
     * Update submission rating
     * Producer-only endpoint
     */
    updateRating: async (payload: {
      submissionId: string;
      rating: undefined;
    }): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "POST",
        `/api/producer/submissions/${payload.submissionId}/rating`,
        { data: payload }
      );
    },

    /**
     * Toggle favorite status of submission
     * Producer-only endpoint
     */
    toggleFavorite: async (payload: {
      submissionId: string;
    }): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "POST",
        `/api/producer/submissions/${payload.submissionId}/favorite`
      );
    },

    /**
     * Edit submission text
     * Producer-only endpoint
     */
    editSubmissionText: async (payload: {
      submissionId: string;
      newText: string;
    }): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "PATCH",
        `/api/producer/submissions/${payload.submissionId}/text`,
        { data: payload }
      );
    },

    /**
     * Delete a submission
     * Producer-only endpoint
     */
    deleteSubmission: async (payload: {
      submissionId: string;
    }): Promise<Types.DeleteOperationResponse> => {
      return this.request(
        "DELETE",
        `/api/producer/submissions/${payload.submissionId}`
      );
    },

    /**
     * Mark submission as broadcast
     * Producer-only endpoint
     */
    markAsBroadcast: async (payload: {
      submissionId: string;
      showSlug: string;
    }): Promise<Types.MarkAsBroadcastResponse> => {
      return this.request(
        "POST",
        `/api/producer/submissions/${payload.submissionId}/broadcast`,
        { data: payload }
      );
    },

    /**
     * Clear broadcast status from submission
     * Producer-only endpoint
     */
    clearBroadcast: async (payload: {
      submissionId: string;
    }): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "DELETE",
        `/api/producer/submissions/${payload.submissionId}/broadcast`
      );
    },

    /**
     * Update submission details (e.g., confidence score)
     * Producer-only endpoint
     */
    updateSubmissionDetails: async (payload: {
      submissionId: string;
      details: any;
    }): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "PATCH",
        `/api/producer/submissions/${payload.submissionId}/details`,
        { data: payload }
      );
    },
  };

  /**
   * Authentication endpoints
   */
  readonly auth = {
    /**
     * Login with email and password
     * Public endpoint
     * Automatically sets the auth token on successful login
     */
    login: async (
      email: string,
      password: string
    ): Promise<Types.LoginResponse> => {
      const response = await this.request<Types.LoginResponse>(
        "POST",
        "/api/auth/login",
        { email, password }
      );
      
      // Automatically set the token after successful login
      if (response.token) {
        this.setAuthToken(response.token);
      }
      
      return response;
    },

    /**
     * Logout current user
     * Authenticated endpoint
     * Automatically clears the auth token
     */
    logout: async (): Promise<Types.OperationResponse> => {
      const response = await this.request<Types.OperationResponse>(
        "POST",
        "/api/auth/logout"
      );
      
      // Clear the token after successful logout
      this.clearAuthToken();
      
      return response;
    },

    /**
     * Get current user info
     * Authenticated endpoint
     */
    getCurrentUser: async (): Promise<Types.User> => {
      return this.request("GET", "/api/auth/me");
    },

    /**
     * Refresh the authentication token
     * Authenticated endpoint
     * Automatically updates the auth token
     */
    refresh: async (): Promise<{ token: string }> => {
      const response = await this.request<{ token: string }>(
        "POST",
        "/api/auth/refresh"
      );
      
      // Automatically set the new token
      if (response.token) {
        this.setAuthToken(response.token);
      }
      
      return response;
    },
  };

  /**
   * Health check endpoint
   */
  readonly health = {
    /**
     * Check API health
     * Public endpoint
     */
    check: async (): Promise<{ status: string; timestamp: number }> => {
      return this.request("GET", "/api/health");
    },
  };

  /**
   * Host endpoints
   * Provides host-specific API calls for the live show views
   */
  readonly host = {
    /**
     * Get hydrated state for the host dashboard in a single request
     * Aggregates active episode, ratings, producer picks, and trending topics
     * Host-only endpoint (requires producer role)
     */
    getHydratedState: async (
      payload: Types.GetHostHydratedStateRequest
    ): Promise<Types.GetHostHydratedStateResponse> => {
      const params: Record<string, string | number | boolean> = {
        showSlug: payload.showSlug,
      };

      return this.request("GET", "/api/host/hydrated-state", undefined, { params });
    },

    /**
     * Get favorited submissions for polling updates
     * Returns favorited submissions that haven't been broadcast
     * Host-only endpoint
     */
    getFavoritedSubmissions: async (
      payload: Types.GetFavoritedSubmissionsRequest
    ): Promise<Types.GetFavoritedSubmissionsResponse> => {
      const params: Record<string, string | number | boolean> = {
        episodeId: payload.episodeId,
      };

      if (payload.limit) {
        params.limit = payload.limit;
      }

      return this.request("GET", "/api/host/favorited-submissions", undefined, { params });
    },

    /**
     * Update submission rating
     * Host-only endpoint
     */
    updateRating: async (payload: {
      submissionId: string;
      rating?: string;
    }): Promise<Types.RatingOperationResponse> => {
      return this.request(
        "POST",
        `/api/host/submissions/${payload.submissionId}/rating`,
        { data: payload }
      );
    },

    /**
     * Mark submission as broadcast
     * Host-only endpoint
     */
    markAsBroadcast: async (payload: {
      submissionId: string;
      showSlug: string;
    }): Promise<Types.MarkAsBroadcastResponse> => {
      return this.request(
        "POST",
        `/api/host/submissions/${payload.submissionId}/broadcast`,
        { data: payload }
      );
    },

    /**
     * Set the current take being broadcast
     * Host-only endpoint
     */
    setCurrentTake: async (payload: {
      submissionId: string;
      showSlug: string;
    }): Promise<Types.SetCurrentTakeResponse> => {
      return this.request(
        "POST",
        `/api/host/submissions/${payload.submissionId}/set-current`,
        { data: payload }
      );
    },

    /**
     * Log a live broadcast event
     * Host-only endpoint
     */
    logBroadcastEvent: async (payload: {
      eventString: string;
      submissionId?: string;
      showSlug: string;
    }): Promise<Types.OperationResponse> => {
      return this.request(
        "POST",
        "/api/host/broadcast/log-event",
        { data: payload }
      );
    },

    /**
     * Get submissions filtered by topic (sport or category)
     * Returns submissions sorted by rating descending
     * Host-only endpoint
     */
    getSubmissionsByTopic: async (payload: {
      showId: string;
      episodeId: string;
      topic: string;
      limit?: number;
    }): Promise<Types.SubmissionSearchResponse> => {
      const params: Record<string, string | number> = {
        showId: payload.showId,
        episodeId: payload.episodeId,
        topic: payload.topic,
      };

      if (payload.limit) {
        params.limit = payload.limit;
      }

      return this.request("GET", "/api/host/submissions/by-topic", undefined, { params });
    },

    /**
     * Get the current take being displayed on broadcast
     * Public endpoint - no authentication required
     */
    getBroadcastCurrent: async (payload: {
      showSlug: string;
    }): Promise<Types.GetBroadcastCurrentResponse> => {
      const params: Record<string, string> = {
        showSlug: payload.showSlug,
      };

      return this.request("GET", "/api/host/broadcast/current", undefined, { params });
    },

    /**
     * Get live events for broadcast (timers, etc.)
     * Public endpoint - no authentication required
     */
    getBroadcastLiveEvents: async (payload: {
      showSlug: string;
    }): Promise<Types.LiveEventData> => {
      const params: Record<string, string> = {
        showSlug: payload.showSlug,
      };

      return this.request("GET", "/api/host/broadcast/live-events", undefined, { params });
    },

    /**
     * Get a single submission by ID
     * Public endpoint - no authentication required
     */
    getSubmission: async (submissionId: string): Promise<Types.Submission> => {
      return this.request("GET", `/api/submissions/${submissionId}`);
    },
  };
}

/**
 * Custom error class for API client errors
 */
export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, any>
  ) {
    super(message);
    this.name = "ApiClientError";
  }

  /**
   * Check if the error is a client error (4xx)
   */
  isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Check if the error is a server error (5xx)
   */
  isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Check if the error is due to authentication/authorization
   */
  isAuthError(): boolean {
    return this.code === Constants.API_ERROR_CODES.AUTHENTICATION_REQUIRED ||
           this.code === Constants.API_ERROR_CODES.PERMISSION_DENIED ||
           this.status === 401 ||
           this.status === 403;
  }

  /**
   * Check if the error is due to rate limiting
   */
  isRateLimitError(): boolean {
    return this.code === Constants.API_ERROR_CODES.RATE_LIMIT_EXCEEDED ||
           this.status === 429;
  }
}

/**
 * Factory function to create a configured API client
 * @example
 * const client = createApiClient('https://api.getserious.com', 'your-api-key');
 */
export function createApiClient(
  baseUrl: string,
  apiKey?: string,
  options?: Omit<ApiClientConfig, "baseUrl" | "apiKey">
): GetSeriousApiClient {
  return new GetSeriousApiClient({
    baseUrl,
    apiKey,
    ...options,
  });
}

/**
 * Create a client for development/testing with local backend
 */
export function createLocalApiClient(): GetSeriousApiClient {
  return createApiClient(
    process.env.REACT_APP_API_URL || ""
  );
}

/**
 * Create a client for production environment
 */
export function createProductionApiClient(apiKey: string): GetSeriousApiClient {
  return createApiClient(
    process.env.REACT_APP_API_URL || "",
    apiKey
  );
}
